import { apiClient, getErrorMessage } from '../../utils/apiClient';
import { logger } from '../../utils/logger';
import { toast } from '../../utils/toast';
import {
  API_PACK_ANALYSIS_PACKAGING_RULE_RESOLVE,
  API_PACK_ANALYSIS_PACKAGING_RULE_LIST,
  API_PACK_ANALYSIS_PACKAGING_RULE_CREATE,
  API_PACK_ANALYSIS_PACKAGING_RULE_SUPERSEDE,
  API_PACK_ANALYSIS_PACKAGING_RULE_DELETE,
  API_PACK_ANALYSIS_PACKAGING_RULE_APPROVED_CARTONS,
  API_PACK_ANALYSIS_CARTON_TYPES_SCOPE_OVERRIDE,
} from '../../constants/apiConstants';

// ============================================================================
// Approved Cartons Admin — sub-resource of PackagingRuleProfile
// Spec: pack-analysis-approved-cartons-admin
// ============================================================================

/** Profile audit metadata returned with the approved-cartons sub-resource. */
export interface ApprovedCartonsProfileMeta {
  profileId: number;
  scopePath: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  createdBy: string | null;
  createdAt: string | null;
}

/**
 * One approved-carton row — join row metadata + the flattened CartonType
 * physical attributes for FE rendering. `fingerprint` is exposed for
 * completeness but the admin UI hides it (it's the internal natural key).
 */
export interface ApprovedCartonListItem {
  id: number;
  cartonTypeId: number;
  fingerprint: string;
  description: string | null;
  category: string | null;
  length: number;
  width: number;
  height: number;
  dimensionUnit: string;
  weightCapacity: number | null;
  weightUnit: string | null;
  tareWeight: number | null;
  volume: number | null;
  volumeUnit: string | null;
  rank: number;
  isActive: boolean;

  /** Buyer-scope display overrides (Spec pack-analysis-approved-cartons-admin
   * scope-override extension). Null when no override set for the requested
   * scope — FE renders `descriptionOverride ?? description`. */
  descriptionOverride?: string | null;
  categoryOverride?: string | null;
}

export interface ApprovedCartonsResponse {
  profileMeta: ApprovedCartonsProfileMeta;
  approvedCartons: ApprovedCartonListItem[];
}

// ============================================================================
// Types — mirror SmartMatrix.Domain.PackAnalysis.Messages.PackagingRuleProfile_*.
// 13 carry-forward fields + 5 new fields, all nullable on the wire so partial
// rows can be authored.
// ============================================================================

/** Effective merged profile (per-FIELD strictest-wins). */
export interface PackagingRuleProfile {
  id: number;
  scopePath: string;
  scopeLevel: number;

  // Carry-forward (13)
  allowColorMix: boolean;
  allowStyleMix: boolean;
  allowCareGroupMix: boolean;
  allowCooMix: boolean;
  allowSizeMix: boolean;
  maxItemsPerCarton?: number | null;
  maxFreeMixPctPerShipment?: number | null;
  maxCartonWeightKg?: number | null;
  gohOrientationRulesJson?: string | null;
  customConstraintsJson?: string | null;
  minCartonFillPct?: number | null;
  maxLineOvershipTolerancePct?: number | null;
  maxOrderOvershipTolerancePct?: number | null;

  // Spec pack-analysis-rounding-policy-two-axis — direction-only after the
  // 2-axis split. Writes: ROUND_UP / ROUND_DOWN / NEAREST + null.
  // Reads: may still surface legacy ROUND_UP_TO_INNER / ROUND_UP_TO_CASE
  // for un-superseded rows; UI normalises via normaliseStoredRoundingPolicy.
  roundingPolicy?: string | null;
  // INNER / CASE + null. Required when policy=ROUND_UP; must be null
  // otherwise. Validator enforces server-side.
  roundingTargetUom?: string | null;
  cartonPolicy?: string | null;          // OPEN / APPROVED_LIST_ONLY / APPROVED_LIST_PREFERRED
  source?: string | null;

  /**
   * Spec X (pack-analysis-buyer-pattern-cascade) — when `true`, BulkOvershipStrategy
   * falls back from strict (buyer, supplier, item) PackMaster matching to a
   * buyer-only (buyer, *, item) aggregation. Default `false`.
   */
  buyerPatternCascade?: boolean;

  /**
   * Spec pack-analysis-exact-pack-mode — when `true`, BulkOvershipStrategy AND
   * PrepackRoundupStrategy emit the residual qty into a partial last carton
   * (matching ML1 1.0 / AutoPack 1.0 semantics) instead of either rounding up
   * to a full carton (overship) or leaving residual unpacked. Bypasses
   * `MinCartonFillPct`. Default `false`. Originally bulk-only; extended to
   * prepack by spec pack-analysis-prepack-multi-pattern-mix (2026-05-01).
   */
  emitPartialLastCarton?: boolean;

  /**
   * Spec pack-analysis-prepack-multi-pattern-mix — when `true`,
   * PackGroupKeyPrepackResolver enumerates ≥2-candidate combinations and picks
   * the lowest-L1-deviation multiset for one PackGroupKey (mirrors SAP S/4HANA
   * BOM-variant allocation). Default `false`.
   */
  allowMultiPatternPrepackMix?: boolean;

  /**
   * Spec pack-analysis-bulk-multi-pattern-mix — when `true`,
   * BulkOvershipStrategy enumerates ≥2 historical PackQty variants for one
   * (buyer, supplier, item, uom) tuple and picks the lowest-L1-deviation
   * multiset (e.g. 3×#2 (qty 25) instead of 3×#1 (qty 30) with overship).
   * Bulk analog of allowMultiPatternPrepackMix. Default `false`.
   */
  allowMultiPatternBulkMix?: boolean;

  /**
   * Spec pack-analysis-prepack-buyer-cascade — when `true`,
   * PackGroupKeyPrepackResolver (the direct-key prepack resolver only — NOT
   * ObservedHistoryPrepackResolver) may, on a strict (tenant, buyer, supplier,
   * "PREPACK:"+code) miss, fall back to the best buyer-only (tenant, buyer, *,
   * "PREPACK:"+code) row across suppliers — an APPROXIMATE suggestion only
   * (confidence capped, requiresAcknowledgment, tier T0PB, reason code
   * MATCHED_PREPACK_BY_GROUP_KEY_CROSS_SUPPLIER, visible warning). Deliberately
   * weaker than `buyerPatternCascade` (bulk). Default `false`.
   */
  prepackBuyerCascade?: boolean;

  /** Spec mlinkone-buyer-require-inner-pack — buyer mandates an inner-pack qty for bulk packing
   *  (Create Packing shows + requires a Qty-Per-Inner input per bulk SKU line). Default `false`. */
  requireInnerPack?: boolean;

  /**
   * Spec pack-analysis-constraint-solver-v1 — Free-Mix bin-packing solver
   * opt-in. `'Independent'` (default) — solver short-circuits with
   * `MIX_PACK_OPT_OUT`. `'AllowMixed'` — solver runs at chain Order=4 (still
   * per-call gateable via `Cartonization_Suggest_Request.AllowMixPack`).
   */
  mixPackPolicy?: 'Independent' | 'AllowMixed' | null;

  /**
   * Spec pack-analysis-constraint-solver-v1 — JSON array of JsonLogic mix
   * predicates (e.g. `[{"all_same":"style"}]`) evaluated per FFD placement
   * attempt. `null` = no mix predicates (free-mix subject only to weight cap).
   */
  mixRules?: string | null;

  /**
   * Spec pack-analysis-constraint-solver-v1 — RESERVED v2. JSON array of
   * `sm_pac_carton_type.id` the solver may pick from. `null` = any (default
   * cascade applies). Stored but NOT consulted by v1 logic.
   */
  allowedCartonTypeIds?: string | null;

  /** Spec pack-analysis-rounding-emission-redesign — per-buyer approved
   * CartonType list resolved alongside this profile (when BE eager-loads).
   * Empty/missing = T1 Stage 4 disabled. Reflects current `rank`-ordered
   * snapshot from `sm_pac_packaging_rule_profile_carton`. */
  approvedCartonTypeIds?: number[] | null;

  effectiveFrom: string;
  effectiveTo?: string | null;
  isActive: boolean;
  internalRemark?: string | null;
  createdAt?: string | null;
  createdBy?: string | null;
  modifiedAt?: string | null;
  modifiedBy?: string | null;
}

/** One row in the admin list — same shape as resolve payload. */
export type PackagingRuleRow = PackagingRuleProfile;

export interface PackagingRuleResolveParams {
  tenantId: number;
  buyerPartyId?: number | null;
  supplierPartyId?: number | null;
  channelCode?: string | null;
  asOfUtc?: string | null;            // ISO; defaults server-side to UtcNow
}

export interface PackagingRuleListFilters {
  tenantId: number;
  scopePathPrefix?: string;
  scopeLevel?: number;
  includeExpired?: boolean;
  pageIndex?: number;
  pageSize?: number;
}

export interface PackagingRuleListResponse {
  items: PackagingRuleRow[];
  pageIndex: number;
  pageSize: number;
  totalCount: number;
}

export interface PackagingRuleCreateRequest {
  scopePath: string;                  // server validates + computes scope_level

  // Carry-forward (13) — nullable booleans default to schema defaults.
  allowColorMix?: boolean | null;
  allowStyleMix?: boolean | null;
  allowCareGroupMix?: boolean | null;
  allowCooMix?: boolean | null;
  allowSizeMix?: boolean | null;
  maxItemsPerCarton?: number | null;
  maxFreeMixPctPerShipment?: number | null;
  maxCartonWeightKg?: number | null;
  gohOrientationRulesJson?: string | null;
  customConstraintsJson?: string | null;
  minCartonFillPct?: number | null;
  maxLineOvershipTolerancePct?: number | null;
  maxOrderOvershipTolerancePct?: number | null;

  // New (5)
  roundingPolicy?: string | null;
  roundingTargetUom?: string | null;
  cartonPolicy?: string | null;
  source?: string | null;

  /** Spec X — buyer-level pattern cascade opt-in. Default `false`. */
  buyerPatternCascade?: boolean;

  /** Spec pack-analysis-exact-pack-mode — partial-last-carton opt-in. Default `false`. */
  emitPartialLastCarton?: boolean;

  /** Spec pack-analysis-prepack-multi-pattern-mix — multi-pattern solver opt-in. Default `false`. */
  allowMultiPatternPrepackMix?: boolean;

  /** Spec pack-analysis-bulk-multi-pattern-mix — bulk-side multi-pattern solver opt-in. Default `false`. */
  allowMultiPatternBulkMix?: boolean;

  /** Spec pack-analysis-prepack-buyer-cascade — PREPACK-side cross-supplier cascade opt-in (approximate suggestion). Default `false`. */
  prepackBuyerCascade?: boolean;

  /** Spec mlinkone-buyer-require-inner-pack — buyer inner-pack mandate opt-in. Default `false`. */
  requireInnerPack?: boolean;

  /** Spec pack-analysis-constraint-solver-v1 — Free-Mix solver opt-in. `null`/omitted defers to schema default `'Independent'`. */
  mixPackPolicy?: 'Independent' | 'AllowMixed' | null;
  /** Spec pack-analysis-constraint-solver-v1 — JSON array of JsonLogic mix predicates. `null` = no mix predicates. */
  mixRules?: string | null;
  /** Spec pack-analysis-constraint-solver-v1 — RESERVED v2 carton-type allow-list (JSON). */
  allowedCartonTypeIds?: string | null;

  /** Spec pack-analysis-rounding-emission-redesign R-5 / R-6 — per-buyer
   * approved CartonType list joined to this profile in
   * `sm_pac_packaging_rule_profile_carton`. Order = operator-curated rank.
   * Each id must reference an active `sm_edi_carton_type.id`; handler
   * validator rejects non-existent or inactive ids. `null` or empty = no
   * approved list (T1 Stage 4 soft-rejects). */
  approvedCartonTypeIds?: number[] | null;

  effectiveFrom: string;
  effectiveTo?: string | null;
  internalRemark?: string | null;
  createdBy?: string | null;

  // For cache eviction post-write
  buyerPartyId?: number | null;
}

/**
 * Spec pack-analysis-approved-cartons-admin Phase E perf — supersede
 * response now carries the post-write approved-cartons payload inline
 * for Add / Remove / Reorder paths so the FE thunk can skip the chained
 * GET round-trip. Null when the supersede took the carry-forward branch
 * (handler doesn't have prior carton-type entities loaded) — caller
 * falls back to the chained `getApprovedCartons` GET.
 */
export interface PackagingRuleSupersedeResult {
  newId: number;
  expiredId: number;
  /** Optional inline refresh — when non-null, has the same shape as
   *  `ApprovedCartonsResponse` so callers can plug it straight into Redux. */
  refreshedApprovedCartons?: {
    profileId: number;
    scopePath: string;
    effectiveFrom: string;
    effectiveTo: string | null;
    createdBy: string | null;
    createdAt: string | null;
    approvedCartons: ApprovedCartonListItem[];
  } | null;
}

export interface PackagingRuleSupersedeRequest {
  newEffectiveFrom: string;
  newEffectiveTo?: string | null;

  newAllowColorMix?: boolean | null;
  newAllowStyleMix?: boolean | null;
  newAllowCareGroupMix?: boolean | null;
  newAllowCooMix?: boolean | null;
  newAllowSizeMix?: boolean | null;
  newMaxItemsPerCarton?: number | null;
  newMaxFreeMixPctPerShipment?: number | null;
  newMaxCartonWeightKg?: number | null;
  newGohOrientationRulesJson?: string | null;
  newCustomConstraintsJson?: string | null;
  newMinCartonFillPct?: number | null;
  newMaxLineOvershipTolerancePct?: number | null;
  newMaxOrderOvershipTolerancePct?: number | null;

  newRoundingPolicy?: string | null;
  newRoundingTargetUom?: string | null;
  newCartonPolicy?: string | null;
  newSource?: string | null;

  /**
   * Spec pack-analysis-rounding-policy-two-axis (Case D 2026-05-14) —
   * explicit clear signal disambiguates "user picked INHERIT to clear" from
   * "user didn't touch rounding". When true, the BE sets BOTH
   * RoundingPolicy and RoundingTargetUom to null on the new row, bypassing
   * the `??` carry-forward. When false/omitted, normal pair-semantics apply.
   */
  clearRoundingPolicy?: boolean | null;

  /** Spec X — null/omitted carries the existing row's cascade value forward. */
  newBuyerPatternCascade?: boolean | null;

  /** Spec pack-analysis-exact-pack-mode — null/omitted carries existing value forward. */
  newEmitPartialLastCarton?: boolean | null;

  /** Spec pack-analysis-prepack-multi-pattern-mix — null/omitted carries existing value forward. */
  newAllowMultiPatternPrepackMix?: boolean | null;

  /** Spec pack-analysis-bulk-multi-pattern-mix — null/omitted carries existing value forward. */
  newAllowMultiPatternBulkMix?: boolean | null;

  /** Spec pack-analysis-prepack-buyer-cascade — null/omitted carries existing value forward. */
  newPrepackBuyerCascade?: boolean | null;

  /** Spec mlinkone-buyer-require-inner-pack — null/omitted carries existing value forward. */
  newRequireInnerPack?: boolean | null;

  /** Spec pack-analysis-constraint-solver-v1 — null carries existing forward; explicit overrides on supersede. */
  newMixPackPolicy?: 'Independent' | 'AllowMixed' | null;
  /** Spec pack-analysis-constraint-solver-v1 — null carries forward; explicit string overrides. */
  newMixRules?: string | null;
  /** Spec pack-analysis-constraint-solver-v1 — RESERVED. null carries forward. */
  newAllowedCartonTypeIds?: string | null;

  /** Spec pack-analysis-rounding-emission-redesign — Supersede 3-branch
   * pair-semantics for the approved CartonType list:
   *   `clearApprovedCartonTypes=true` → new profile has empty list
   *   `approvedCartonTypeIds` non-null → replace verbatim
   *   both null/omitted → carry forward from prior profile (Clone)
   */
  approvedCartonTypeIds?: number[] | null;
  clearApprovedCartonTypes?: boolean | null;

  newInternalRemark?: string | null;
  modifiedBy?: string | null;
  buyerPartyId?: number | null;
  /** Cache eviction hint — the resolver caches by (tenant, buyer, supplier, channel),
   * so supplying this lets the BE drop the supplier-scoped key immediately
   * instead of waiting for the 60s TTL. */
  supplierPartyId?: number | null;
}

interface ResultEnvelope<T> {
  succeeded: boolean;
  statusCode?: number;
  messages?: string[];
  data?: T;
}

interface PackagingRuleResolveEnvelope {
  profile: PackagingRuleProfile | null;
}

// ============================================================================
// Service
// ============================================================================

export const packagingRuleService = {
  async resolve(params: PackagingRuleResolveParams): Promise<PackagingRuleProfile | null> {
    try {
      const response = await apiClient.get<ResultEnvelope<PackagingRuleResolveEnvelope>>(
        API_PACK_ANALYSIS_PACKAGING_RULE_RESOLVE,
        {
          params: {
            tenantId: params.tenantId,
            buyerPartyId: params.buyerPartyId ?? undefined,
            supplierPartyId: params.supplierPartyId ?? undefined,
            channelCode: params.channelCode ?? undefined,
            asOfUtc: params.asOfUtc ?? undefined,
          },
        }
      );
      // -2410 NOT_FOUND is also surfaced as succeeded=false; treat as null cache miss.
      if (!response.data.succeeded) return null;
      return response.data.data?.profile ?? null;
    } catch (error) {
      logger.error('Failed to resolve packaging-rule profile', error);
      throw error;
    }
  },

  async list(filters: PackagingRuleListFilters): Promise<PackagingRuleListResponse> {
    try {
      const response = await apiClient.get<ResultEnvelope<PackagingRuleListResponse>>(
        API_PACK_ANALYSIS_PACKAGING_RULE_LIST,
        {
          params: {
            tenantId: filters.tenantId,
            scopePathPrefix: filters.scopePathPrefix || undefined,
            scopeLevel: filters.scopeLevel,
            includeExpired: filters.includeExpired,
            pageIndex: filters.pageIndex ?? 1,
            pageSize: filters.pageSize ?? 50,
          },
        }
      );
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', ') || 'Failed to list packaging rules');
      return response.data.data ?? { items: [], pageIndex: 1, pageSize: 50, totalCount: 0 };
    } catch (error) {
      logger.error('Failed to list packaging-rule profiles', error);
      throw error;
    }
  },

  /**
   * Revision-history fetch for an EXACT scope. Spec
   * `pack-analysis-approved-cartons-admin` Phase H / Req 6.1-6.4.
   *
   * Calls the existing list endpoint with `includeExpired=true` and a
   * scope-prefix filter, then post-filters to rows whose `scopePath`
   * equals the requested scope. The BE applies a LIKE prefix match so
   * sibling scopes (e.g. `/buyer:VNV/supplier:SUP-A` vs `SUP-A2`) could
   * otherwise leak through. The BE handler already orders the results
   * `scope_level ASC, scope_path ASC, effective_from DESC` — newest-first
   * per scope — which is what the revision-history UI wants.
   *
   * scope_level is OMITTED on purpose: the project's scope_path convention
   * (`project_scope_path_convention.md`) makes deriving it from the path
   * client-side error-prone (`/global` = 0; everything else = segment
   * count). The BE treats null as "any level", and the exact-scopePath
   * post-filter below is sufficient to pin a single scope.
   */
  async listProfileHistory(
    tenantId: number,
    scopePath: string,
  ): Promise<PackagingRuleRow[]> {
    const response = await this.list({
      tenantId,
      scopePathPrefix: scopePath,
      includeExpired: true,
      pageIndex: 1,
      pageSize: 100,
    });
    return response.items.filter((p) => p.scopePath === scopePath);
  },

  async create(tenantId: number, request: PackagingRuleCreateRequest): Promise<{ id: number }> {
    try {
      const response = await apiClient.post<ResultEnvelope<{ id: number }>>(
        API_PACK_ANALYSIS_PACKAGING_RULE_CREATE,
        request,
        { params: { tenantId } }
      );
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', ') || 'Failed to create packaging-rule profile');
      toast.success('Packaging-rule profile created');
      return response.data.data ?? { id: 0 };
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to create packaging-rule profile: ${msg}`);
      throw error;
    }
  },

  async supersede(
    tenantId: number,
    id: number,
    request: PackagingRuleSupersedeRequest,
    options?: { silent?: boolean },
  ): Promise<PackagingRuleSupersedeResult> {
    // One transparent retry on `TaskCanceledException` — the BE supersede
    // write-path triggers an AAD DefaultAzureCredential token refresh that
    // stalls past the SqlClient command timeout on cold starts (per
    // `feedback_aad_default_credential_dev_hang.md` Trap 4). The first
    // attempt fails BEFORE the DB write (the token refresh never resolves),
    // so retry-once is safe — no risk of a duplicate version. After the
    // first cancellation the AAD token is warm and the retry typically
    // succeeds in normal latency.
    const attempt = async (): Promise<PackagingRuleSupersedeResult> => {
      const response = await apiClient.put<ResultEnvelope<PackagingRuleSupersedeResult>>(
        API_PACK_ANALYSIS_PACKAGING_RULE_SUPERSEDE(id),
        request,
        { params: { tenantId } }
      );
      if (!response.data.succeeded) {
        throw new Error(response.data.messages?.join(', ') || 'Failed to supersede packaging-rule profile');
      }
      return response.data.data ?? { newId: 0, expiredId: id };
    };
    const isCancellation = (msg: string): boolean => {
      const m = msg.toLowerCase();
      return m.includes('task was canceled')
        || m.includes('task was cancelled')
        || m.includes('operation was canceled')
        || m.includes('operation was cancelled');
    };
    try {
      let result: PackagingRuleSupersedeResult;
      try {
        result = await attempt();
      } catch (e) {
        if (isCancellation(getErrorMessage(e))) {
          // Warm-up window — give AAD ~600ms to resolve before retrying.
          await new Promise((resolve) => setTimeout(resolve, 600));
          result = await attempt();
        } else {
          throw e;
        }
      }
      if (!options?.silent) toast.success('Packaging-rule profile superseded');
      return result;
    } catch (error) {
      const msg = getErrorMessage(error);
      // `silent` suppresses BOTH the success toast (per intent='reorder')
      // AND the raw-error toast — callers using silent are expected to
      // surface their own human-readable error UX (e.g. the reorder
      // "...reverted to previous order" toast in ApprovedCartonsAdmin).
      if (!options?.silent) {
        toast.error(`Failed to supersede packaging-rule profile: ${msg}`);
      }
      throw error;
    }
  },

  /**
   * Sub-resource fetch: approved-carton list for a specific profile id.
   * Spec pack-analysis-approved-cartons-admin Req 1 — returns join-row rows
   * with their CartonType physical attributes eager-loaded in a single
   * round-trip.
   *
   * `scope` is optional — when both `tenantId` and `buyerPartyId` are
   * supplied the BE additionally surfaces buyer-scope display overrides
   * (description / category) per row.
   */
  async getApprovedCartons(
    profileId: number,
    scope?: { tenantId?: number | null; buyerPartyId?: number | null },
  ): Promise<ApprovedCartonsResponse> {
    try {
      const response = await apiClient.get<ResultEnvelope<{
        profileId: number;
        scopePath: string;
        effectiveFrom: string;
        effectiveTo: string | null;
        createdBy: string | null;
        createdAt: string | null;
        approvedCartons: ApprovedCartonListItem[];
      }>>(API_PACK_ANALYSIS_PACKAGING_RULE_APPROVED_CARTONS(profileId), {
        params: {
          tenantId: scope?.tenantId ?? undefined,
          buyerPartyId: scope?.buyerPartyId ?? undefined,
        },
      });

      if (!response.data.succeeded) {
        throw new Error(response.data.messages?.join(', ') || 'Failed to load approved cartons');
      }
      const data = response.data.data;
      if (!data) {
        throw new Error('Empty response from getApprovedCartons');
      }
      return {
        profileMeta: {
          profileId: data.profileId,
          scopePath: data.scopePath,
          effectiveFrom: data.effectiveFrom,
          effectiveTo: data.effectiveTo,
          createdBy: data.createdBy,
          createdAt: data.createdAt,
        },
        approvedCartons: data.approvedCartons ?? [],
      };
    } catch (error) {
      logger.error('Failed to load approved cartons', error);
      throw error;
    }
  },

  /**
   * Convenience wrapper around `supersede` for the Approved Cartons admin
   * page. Sends a minimal supersede payload that ONLY touches the approved-
   * carton list — all other profile fields carry forward via BE's null
   * defaults. Spec pack-analysis-approved-cartons-admin Req 2.5 / 3.2 / 4.2.
   *
   * `hadPriorList` is required so we can correctly set
   * `clearApprovedCartonTypes` when the new array is empty (the BE 3-branch
   * pair-semantics: `approvedCartonTypeIds=[] + clearApprovedCartonTypes=true`
   * means "explicitly clear", whereas both null means "carry forward").
   */
  async supersedeWithApprovedList(
    tenantId: number,
    profileId: number,
    newApprovedIds: number[],
    hadPriorList: boolean,
    scope: { buyerPartyId?: number | null; supplierPartyId?: number | null },
    options?: { silent?: boolean },
  ): Promise<PackagingRuleSupersedeResult> {
    return this.supersede(
      tenantId,
      profileId,
      {
        newEffectiveFrom: new Date().toISOString(),
        approvedCartonTypeIds: newApprovedIds,
        clearApprovedCartonTypes: newApprovedIds.length === 0 && hadPriorList,
        buyerPartyId: scope.buyerPartyId ?? null,
        supplierPartyId: scope.supplierPartyId ?? null,
      },
      options,
    );
  },

  /**
   * Spec pack-analysis-approved-cartons-admin (scope-override extension) —
   * upsert the buyer-scope display override (description / category) for a
   * single carton type. Stored in `sm_edi_carton_type_alias` with
   * `scope='buyer', scope_ref_id=buyerPartyId, alias_code=NULL`. Blank fields
   * clear the override (master values surface again).
   */
  async upsertCartonScopeOverride(
    tenantId: number,
    cartonTypeId: number,
    buyerPartyId: number,
    descriptionOverride: string | null,
    categoryOverride: string | null,
  ): Promise<void> {
    try {
      const response = await apiClient.put<ResultEnvelope<{ cartonTypeId: number; buyerPartyId: number }>>(
        API_PACK_ANALYSIS_CARTON_TYPES_SCOPE_OVERRIDE(cartonTypeId),
        {
          buyerPartyId,
          descriptionOverride,
          categoryOverride,
        },
        { params: { tenantId } }
      );
      if (!response.data.succeeded) {
        throw new Error(response.data.messages?.join(', ') || 'Failed to save carton scope override');
      }
      toast.success('Carton override saved');
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to save carton override: ${msg}`);
      throw error;
    }
  },

  async remove(tenantId: number, id: number): Promise<void> {
    try {
      const response = await apiClient.delete<ResultEnvelope<unknown>>(
        API_PACK_ANALYSIS_PACKAGING_RULE_DELETE(id),
        { params: { tenantId } }
      );
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', ') || 'Failed to delete packaging-rule profile');
      toast.success('Packaging-rule profile deleted');
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to delete packaging-rule profile: ${msg}`);
      throw error;
    }
  },
};
